import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import './media-frame'
import { css, html, type PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { catchError, distinctUntilChanged, EMPTY, finalize, from, map, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs'
import type { PDFWorker } from 'pdfjs-dist'
import type { SchmancyMediaBox, SchmancyMediaFrame } from './media-frame'

// One parser worker serves the visible thumbnails; each loading task owns its document.
let worker: PDFWorker | undefined

/**
 * @element schmancy-pdf-thumbnail
 *
 * A non-interactive first-page raster. The parser loads on demand and works
 * independently of the browser's PDF viewer. The consumer owns the source URL.
 *
 * @fires load - The first page is rendered.
 * @fires load-failed - The PDF could not be rendered; detail is the renderer's error. Not
 *   `error`: a bubbling, composed `error` reaches `window` as an uncaught exception.
 */
@schmancyElement('schmancy-pdf-thumbnail')
export class SchmancyPdfThumbnail extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				pointer-events: none;
			}
		`,
	]

	@property({ type: String }) src = ''
	/** Immutable content identity; renewed URLs for the same bytes keep the rendered page. */
	@property({ type: String, attribute: 'content-key' }) contentKey = ''
	@property({ attribute: false }) box?: SchmancyMediaBox
	@property({ type: Number, attribute: 'max-width' }) maxWidth = Number.POSITIVE_INFINITY
	@property({ type: Number, attribute: 'max-height' }) maxHeight = Number.POSITIVE_INFINITY
	@property({ type: String }) label = 'PDF document'
	@property({ type: String, attribute: 'failed-label' }) failedLabel?: string

	/** Consumer status while resolving the URL; rendering status belongs to the frame. */
	@property({ type: String, reflect: true }) state: 'ready' | 'pending' | 'failed' = 'ready'

	private frame = createRef<SchmancyMediaFrame>()
	private requested = new Subject<void>()

	override connectedCallback(): void {
		super.connectedCallback()
		this.requested.pipe(
			startWith(undefined),
			switchMap(() => from(this.updateComplete)),
			map(() => ({ contentKey: this.contentKey, src: this.src, state: this.state })),
			distinctUntilChanged((previous, current) =>
				previous.contentKey === current.contentKey && previous.state === current.state &&
				Boolean(previous.src) === Boolean(current.src) &&
				(previous.src === current.src || (Boolean(current.contentKey) && this.frame.value?.state === 'ready')),
			),
			switchMap(() => {
				const frame = this.frame.value
				if (!frame) return EMPTY
				frame.src = ''
				frame.state = this.state === 'ready' ? (this.src ? 'pending' : 'failed') : this.state
				if (this.state !== 'ready' || !this.src) return EMPTY
				const src = this.src

				return from(import('pdfjs-dist')).pipe(
					switchMap(pdfjs => {
						worker ??= pdfjs.PDFWorker.create({
							port: new Worker(new URL('./pdf.worker.ts', import.meta.url), { type: 'module' }),
						})
						const loading = pdfjs.getDocument({ url: src, worker, disableAutoFetch: true, disableStream: true })
						return from(loading.promise).pipe(
							switchMap(document => from(document.getPage(1))),
							switchMap(page => {
								const bounds = frame.getBoundingClientRect()
								const natural = page.getViewport({ scale: 1 })
								const viewport = page.getViewport({
									// Keep the page sharp when a narrow grid becomes a single column.
									scale: Math.max(
										768 / Math.max(natural.width, natural.height),
										Math.min(
											(bounds.width || this.box?.width || 320) / natural.width,
											(bounds.height || this.box?.height || 320) / natural.height,
										) * Math.min(window.devicePixelRatio || 1, 2),
									),
								})
								const canvas = document.createElement('canvas')
								canvas.width = Math.ceil(viewport.width)
								canvas.height = Math.ceil(viewport.height)
								const rendering = page.render({ canvas, viewport })
								return from(rendering.promise).pipe(
									tap(() => {
										frame.src = canvas.toDataURL('image/webp')
										frame.state = 'ready'
										this.dispatchEvent(new Event('load', { bubbles: true, composed: true }))
									}),
									finalize(() => {
										rendering.cancel()
										canvas.width = 0
										canvas.height = 0
									}),
								)
							}),
							finalize(() => { void loading.destroy() }),
						)
					}),
					catchError(error => {
						frame.state = 'failed'
						this.dispatchEvent(new CustomEvent('load-failed', { detail: error, bubbles: true, composed: true }))
						return EMPTY
					}),
				)
			}),
			takeUntil(this.disconnecting),
		).subscribe()
	}

	protected override updated(changed: PropertyValues): void {
		if (['src', 'contentKey', 'state'].some(property => changed.has(property))) this.requested.next()
	}

	override render() {
		return html`
			<schmancy-media-frame
				${ref(this.frame)}
				class="block h-full rounded-[inherit]"
				.box=${this.box}
				.maxWidth=${this.maxWidth}
				.maxHeight=${this.maxHeight}
				.alt=${this.label}
				.failedLabel=${this.failedLabel ?? componentText('pdfThumbnail.unavailable')}
				state="pending"
			></schmancy-media-frame>
		`
	}
}
declare global {
	interface HTMLElementTagNameMap {
		'schmancy-pdf-thumbnail': SchmancyPdfThumbnail
	}
}
